import { useTranslation } from "react-i18next";
import { Cloud, CloudOff, Loader2 } from "lucide-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useSyncEngine } from "@/hooks/useSyncEngine";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SyncStatusIndicatorProps {
  collapsed?: boolean;
}

export function SyncStatusIndicator({ collapsed = false }: SyncStatusIndicatorProps) {
  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const { pendingCount, isSyncing, syncProgress, isOfflineCapable } = useSyncEngine();

  // Hide completely when online, no pending items, not syncing, and offline not capable
  if (isOnline && pendingCount === 0 && !isSyncing && !isOfflineCapable) {
    return null;
  }

  // Determine state
  const isOffline = !isOnline;
  const hasPending = pendingCount > 0;

  // Only show if there's something relevant to display
  if (isOnline && !hasPending && !isSyncing) {
    return null;
  }

  const icon = isSyncing ? (
    <Loader2 className="h-4 w-4 animate-spin text-primary" />
  ) : isOffline ? (
    <CloudOff className="h-4 w-4 text-destructive" />
  ) : (
    <Cloud className="h-4 w-4 text-muted-foreground" />
  );

  const label = isSyncing
    ? syncProgress
      ? `${t("common.syncing")} ${syncProgress.current}/${syncProgress.total}`
      : t("common.syncing")
    : isOffline
      ? t("common.offline")
      : "";

  const pendingLabel = hasPending ? t("common.pendingChanges", { count: pendingCount }) : "";

  const tooltipText = [label, pendingLabel].filter(Boolean).join(" – ");

  const content = (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors",
        isOffline
          ? "bg-destructive/10 text-destructive"
          : isSyncing
            ? "bg-primary/10 text-primary"
            : "bg-muted text-muted-foreground",
        collapsed && "justify-center px-2",
      )}
    >
      {icon}
      {!collapsed && (
        <div className="flex flex-col min-w-0">
          {label && <span className="font-medium truncate">{label}</span>}
          {pendingLabel && <span className="text-[10px] opacity-70 truncate">{pendingLabel}</span>}
        </div>
      )}
      {!collapsed && hasPending && !isSyncing && (
        <span className="ml-auto flex-shrink-0 rounded-full bg-destructive/20 text-destructive px-1.5 py-0.5 text-[10px] font-semibold">
          {pendingCount}
        </span>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right">
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
